// PregnancyRiskScreening.jsx
// Production-ready Pregnancy Risk Screening
// Uses: ScreeningLayout.jsx (shared shell) + pregnancyRiskScreeningData.js (JSON config)
import React, { useRef, useState } from 'react';
import {
  AppShell, Card, CardHeader, CardBody, CardFooter,
  ProgressBar, Sidebar,
  Logo, ChevronRight, Check, ReviewRow,
  ToggleGroup, ConcernGrid,
  ResultGauge, ConfidenceBar, NextActionList,
  CrisisAlert,
} from './ScreeningLayout';
import styles from './screening.module.css';
import {
  PREG_STEPS, PREG_FLOW,
  PREG_INTRO, PREG_CONSENT, PREG_CONCERNS,
  PREG_QUESTION_SCREENS, PREG_REVIEW_SECTIONS,
  computePregScore, getPregRisk,
} from './PregRiskScreeningData';
import PregnancyWelcomeWeb from '../PregnancyRiskScreening/PregWelcomeScreen';
// import { ConsentScreen } from '../../pages/CareNavigator';
import { RISK_SCREENING_CONSENT } from './MentalWellnessScreening';
import ConsentScreen from '../Reusablecomponents/Consentscreen/Consentscreen';
// ── Illustrations ─────────────────────────────────────────────────────────────
function PregnancyIllustration() {
  return (
    <svg viewBox="0 0 260 280" fill="none" xmlns="http://www.w3.org/2000/svg" className={styles.illusSvg}>
      {/* Background blobs */}
      <circle cx="145" cy="130" r="105" fill="#FCE7F3" opacity=".50" />
      <ellipse cx="130" cy="210" rx="85" ry="28" fill="#FBCFE8" opacity=".30" />

      {/* Floor shadow */}
      <ellipse cx="128" cy="238" rx="52" ry="8" fill="#F48FB1" opacity=".18" />

      {/* Legs */}
      <path d="M100 200 Q96 225 104 238 Q114 244 128 244 Q142 244 152 238 Q160 225 156 200Z" fill="#F06292" opacity=".75" />

      {/* Body torso */}
      <path d="M100 140 Q96 172 100 200 L156 200 Q160 172 156 140Z" fill="#E91E63" opacity=".80" />

      {/* Pregnant belly — layered circles for depth */}
      <ellipse cx="128" cy="168" rx="36" ry="40" fill="#F48FB1" opacity=".95" />
      <ellipse cx="128" cy="170" rx="28" ry="31" fill="#FCE4EC" opacity=".65" />

      {/* Baby silhouette inside belly */}
      <ellipse cx="128" cy="168" rx="13" ry="17" fill="none" stroke="#E91E63" strokeWidth="1.2" strokeDasharray="3 3" opacity=".35" />
      <circle cx="128" cy="157" r="5.5" fill="#FCE4EC" opacity=".55" />

      {/* Head */}
      <circle cx="128" cy="100" r="25" fill="#F48FB1" />

      {/* Hair */}
      <path d="M106 94 Q110 70 136 72 Q154 78 150 102" fill="#AD1457" />

      {/* Eyes */}
      <ellipse cx="120" cy="100" rx="2.5" ry="3" fill="#AD1457" opacity=".55" />
      <ellipse cx="136" cy="100" rx="2.5" ry="3" fill="#AD1457" opacity=".55" />

      {/* Smile */}
      <path d="M122 109 Q128 114 134 109" stroke="#AD1457" strokeWidth="1.8" strokeLinecap="round" fill="none" />

      {/* Arms resting on belly */}
      <path d="M100 148 Q80 160 78 178" stroke="#F48FB1" strokeWidth="9" strokeLinecap="round" fill="none" />
      <path d="M156 148 Q176 160 178 174" stroke="#F48FB1" strokeWidth="9" strokeLinecap="round" fill="none" />
      <ellipse cx="82" cy="181" rx="10" ry="7" fill="#F8BBD9" transform="rotate(-12 82 181)" />
      <ellipse cx="174" cy="177" rx="10" ry="7" fill="#F8BBD9" transform="rotate(12 174 177)" />

      {/* Heartbeat line above */}
      <path d="M88 76 L96 76 L100 66 L106 86 L112 70 L116 76 L124 76"
        stroke="#E91E63" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity=".8" />

      {/* Sparkle dots */}
      <circle cx="180" cy="86" r="4.5" fill="#F48FB1" opacity=".7" />
      <circle cx="192" cy="108" r="2.8" fill="#FBCFE8" opacity=".8" />
      <circle cx="70" cy="102" r="3.2" fill="#F48FB1" opacity=".6" />
      <circle cx="58" cy="132" r="2" fill="#FBCFE8" opacity=".7" />

      {/* Star sparkle */}
      <path d="M184 56L186 50L188 56L194 58L188 60L186 66L184 60L178 58Z" fill="#F48FB1" opacity=".75" />
    </svg>
  );
}

function ConsentIllustration() {
  return (
    <svg viewBox="0 0 80 80" fill="none" className={styles.consentIllusSvg}>
      <rect x="15" y="10" width="50" height="60" rx="6" fill="#FCE7F3" stroke="#BE185D" strokeWidth="1.5" />
      <rect x="22" y="22" width="36" height="4" rx="2" fill="#BE185D" opacity=".4" />
      <rect x="22" y="32" width="28" height="4" rx="2" fill="#BE185D" opacity=".3" />
      <rect x="22" y="42" width="32" height="4" rx="2" fill="#BE185D" opacity=".3" />
      <circle cx="58" cy="58" r="14" fill="#BE185D" />
      <path d="M52 58L56 62L64 54" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// SUB-SCREENS  —  identical pattern to MentalWellnessScreening
// ══════════════════════════════════════════════════════════════════════════════

function IntroScreen({ config, onStart }) {
  return (
    <CardBody>
      <div className={styles.introWrap}>
        <div className={styles.introContent}>
          <span className={styles.introBadge}>
            <span>{config.badgeIcon}</span> {config.badge}
          </span>
          <h1 className={styles.introHeading}>{config.heading}</h1>
          <p className={styles.introDesc}>{config.desc}</p>
          <ul className={styles.introBullets}>
            {config.bullets.map((b) => (
              <li key={b.text} className={styles.introBullet}>
                <span className={styles.introBulletIcon}>{b.icon}</span>
                <span>{b.text}</span>
              </li>
            ))}
          </ul>
          <div className={styles.introPrivacy}>🛡️ {config.privacy}</div>
          <button className={styles.introStartBtn} onClick={onStart}>
            {config.startLabel} <ChevronRight />
          </button>
          <button className={styles.introLaterBtn}>{config.laterLabel}</button>
        </div>
        <div className={styles.introIllus}>
          <div className={styles.illusBlob} />
          <PregnancyIllustration />
        </div>
      </div>
    </CardBody>
  );
}

// function ConsentScreen({ config, onAgree, onBack }) {
//   const [checked, setChecked] = useState(false);
//   return (
//     <CardBody>
//       <div className={styles.consentWrap}>
//         <div className={styles.consentIllusWrap}><ConsentIllustration /></div>
//         <h2 className={styles.consentHeading}>{config.heading}</h2>
//         {config.paragraphs.map((p, i) => (
//           <p key={i} className={styles.consentText}>{p}</p>
//         ))}
//         <CrisisAlert>
//           If you are experiencing severe pain, heavy bleeding, or any medical emergency, please call emergency services (112 / 108) or go to your nearest A&E immediately.
//         </CrisisAlert>
//         <button
//           className={`${styles.consentCheckBtn} ${checked ? styles.consentCheckBtnActive : ''}`}
//           onClick={() => setChecked((v) => !v)}
//         >
//           <span className={styles.consentBox}>{checked && <Check />}</span>
//           <span>{config.checkLabel}</span>
//         </button>
//         <button className={styles.consentAgreeBtn} disabled={!checked} onClick={onAgree}>
//           {config.agreeLabel} <ChevronRight />
//         </button>
//       </div>
//     </CardBody>
//   );
// }

function ConcernsScreen({ config, selected, onChange, onNext, onBack }) {
  return (
    <>
      <CardBody>
        <div className={styles.screenStepBadge}>
          <span className={styles.screenStepNum}>1</span> Main Concerns
        </div>
        <h2 className={styles.screenTitle}>What are your main concerns?</h2>
        <p className={styles.screenSubtitle}>Select all that apply</p>
        <ConcernGrid options={config} selected={selected} onChange={onChange} />
      </CardBody>
      <CardFooter onBack={onBack} onNext={onNext} nextLabel="Continue" disabled={selected.length === 0} />
    </>
  );
}

// ── QuestionScreen — IDENTICAL to MentalWellnessScreening ────────────────────
// Cycles one question at a time within each screen using ToggleGroup.
// Red-flag answers surface a CrisisAlert inline (e.g. vaginal bleeding, severe pain).
// function QuestionScreen({ screenConfig, answers, onChange, onNext, onBack, questionIndex, totalQuestions }) {
//   const [qIdx, setQIdx] = useState(0);
//   const dateRef = useRef(null);                              // ← ADD
//   const today = new Date().toISOString().split('T')[0];
//   const visible = screenConfig.questions.filter((q) => {
//     if (!q.conditional) return true;
//     const { field, notValue, value } = q.conditional;
//     const fv = answers[field];
//     if (value !== undefined) return fv === value;
//     if (notValue !== undefined) return fv !== notValue;
//     return true;
//   });

//   const current = visible[qIdx];
//   const isLast = qIdx === visible.length - 1;

//   // Red-flag: any question marked redFlag where user answered 'Yes'
//   const isRedFlag = current?.redFlag && answers[current?.id] === 'Yes';

//   const handleNext = () => { if (!isLast) { setQIdx((i) => i + 1); return; } onNext(); };
//   const handleBack = () => { if (qIdx > 0) { setQIdx((i) => i - 1); return; } onBack(); };

//   return (
//     <>
//       <ProgressBar
//         step={questionIndex + 1}
//         total={totalQuestions}
//         label={screenConfig.stepLabel}
//       />
//       <CardBody>
//         <div className={styles.screenStepBadge}>
//           <span className={styles.screenStepNum}>{screenConfig.icon}</span>
//           {screenConfig.stepLabel}
//         </div>
//         <h2 className={styles.screenTitle}>{screenConfig.title}</h2>
//         {qIdx === 0 && <p className={styles.screenSubtitle}>{screenConfig.subtitle}</p>}

//         {/* {current && (
//           <div className={styles.questionBlock}>
//             <label className={styles.questionLabel}>{current.label}</label>
//             <ToggleGroup
//               options={current.options}
//               value={answers[current.id]}
//               onChange={(v) => onChange(current.id, v)}
//             />
//             {isRedFlag && (
//               <CrisisAlert>
//                 This symptom requires urgent attention. Please contact your OB/GYN or go to the nearest emergency department immediately. Call 112 / 108 if you cannot travel safely.
//               </CrisisAlert>
//             )}
//           </div>
//         )} */}
//         {current && (
//           <div className={styles.questionBlock}>
//             <label className={styles.questionLabel}>{current.label}</label>

//             {current.type === 'date' && (
//               <div className={styles.dateInputWrap}>
//                 <input
//                   ref={dateRef}
//                   className={styles.dateInput}
//                   type="date"
//                   max={today}
//                   value={answers[current.id] || ''}
//                   onChange={(e) => onChange(current.id, e.target.value)}
//                 />
//               </div>
//             )}

//             {current.type === 'slider' && (
//               <div className={styles.sliderWrap}>
//                 <input
//                   type="range"
//                   className={styles.slider}
//                   min={current.min ?? 0}
//                   max={current.max ?? 10}
//                   step={current.step ?? 1}
//                   value={answers[current.id] ?? current.defaultValue ?? current.min ?? 0}
//                   onChange={(e) => onChange(current.id, Number(e.target.value))}
//                 />
//                 <div className={styles.sliderMeta}>
//                   <span className={styles.sliderLabel}>{current.minLabel || current.leftLabel || 'Low'}</span>
//                   <span className={styles.sliderValue}>
//                     {answers[current.id] ?? current.defaultValue ?? current.min ?? 0}
//                     {current.unit ? ` ${current.unit}` : ''}
//                   </span>
//                   <span className={styles.sliderLabel}>{current.maxLabel || current.rightLabel || 'High'}</span>
//                 </div>
//               </div>
//             )}

//             {(current.type === 'toggle' || current.type === 'yesNo' || !current.type) && (
//               <ToggleGroup
//                 options={current.options}
//                 value={answers[current.id]}
//                 onChange={(v) => onChange(current.id, v)}
//               />
//             )}

//             {isRedFlag && (
//               <CrisisAlert>
//                 This symptom requires urgent attention. Please contact your OB/GYN or go to the nearest emergency department immediately. Call 112 / 108 if you cannot travel safely.
//               </CrisisAlert>
//             )}
//           </div>
//         )}
//       </CardBody>
//       <CardFooter
//         onBack={handleBack}
//         onNext={handleNext}
//         nextLabel={isLast ? 'Continue' : 'Next'}
//         disabled={!answers[current?.id]}
//       />
//     </>
//   );
// }
function QuestionScreen({ screenConfig, answers, onChange, onNext, onBack, questionIndex, totalQuestions, qIdxMap }) {
  const dateRef = useRef(null);
  const screenId = screenConfig.id;
  const [qIdx, setQIdx] = useState(
     () => qIdxMap?.current?.[screenId] ?? 0
   );
 
  const updateQIdx = (newIdx) => {
     setQIdx(newIdx);
     if (qIdxMap?.current) qIdxMap.current[screenId] = newIdx;
   };
  const today = new Date().toISOString().split('T')[0];

  const visible = screenConfig.questions.filter((q) => {
    if (!q.conditional) return true;
    const { field, notValue, value } = q.conditional;
    const fv = answers[field];
    if (value !== undefined) return fv === value;
    if (notValue !== undefined) return fv !== notValue;
    return true;
  });
   const current = visible[qIdx];
  const isLast = qIdx === visible.length - 1;

  // A question is "required" if it's not a slider (sliders always have a defaultValue)
  const allAnswered = visible.every((q) => {
    if (q.type === 'slider') return true;          // always has a value
    if (q.type === 'date') return !!answers[q.id]; // optional — change to true to skip
    return !!answers[q.id];
  });
  // const handleNext = () => { if (!isLast) { setQIdx((i) => i + 1); return; } onNext(); };
  // const handleBack = () => { if (qIdx > 0) { setQIdx((i) => i - 1); return; } onBack(); };
    const handleNext = () => { if (!isLast) { updateQIdx(qIdx + 1); return; } onNext(); };
  const handleBack = () => { if (qIdx > 0) { updateQIdx(qIdx - 1); return; } onBack(); };
  return (
    <>
      <ProgressBar
        // step={questionIndex + 1}
        // total={totalQuestions}
        step={qIdx}
        total={visible.length}
        label={screenConfig.stepLabel}
      />
      <CardBody>
        <div className={styles.screenStepBadge}>
          <span className={styles.screenStepNum}>{screenConfig.icon}</span>
          {screenConfig.stepLabel}
        </div>
        <div className={styles.questionCounter}>
          <span className={styles.questionCountCurrent}>Question: {qIdx + 1}</span>
          <span className={styles.questionCountSep}>/</span>
          <span className={styles.questionCountTotal}>{visible.length}</span>
        </div>
        {/* <h2 className={styles.screenTitle}>{screenConfig.title}</h2> */}
        <p className={styles.screenSubtitle}>{screenConfig.subtitle}</p>

        {/* {visible.map((q) => {
          const isRedFlag = q.redFlag && answers[q.id] === 'Yes';

          return (
            <div key={q.id} className={styles.questionBlock}>
              <label className={styles.questionLabel}>{q.label}</label> */}
        {current && (() => {
          const q = current;
          const isRedFlag = q.redFlag && answers[q.id] === 'Yes';
          return (
            <div key={q.id} className={styles.questionBlock}>
              <label className={styles.questionLabel}>{q.label}</label>

              {q.type === 'date' && (
                <div className={styles.dateInputWrap}>
                  <input
                    ref={dateRef}
                    className={styles.dateInput}
                    type="date"
                    max={today}
                    value={answers[q.id] || ''}
                    onChange={(e) => onChange(q.id, e.target.value)}
                  />
                </div>
              )}


              {q.type === 'slider' && (() => {
                const val = answers[q.id] ?? q.defaultValue ?? q.min ?? 0;
                const min = q.min ?? 0;
                const max = q.max ?? 10;
                const pct = ((val - min) / (max - min)) * 100;

                return (
                  <div className={styles.sliderWrap}>
                    <div className={styles.sliderRow}>
                      <span className={styles.sliderEndLabel}>{q.minLabel || q.leftLabel || 'Low'}</span>

                      <div className={styles.sliderTrack}>
                        <span
                          className={styles.sliderVal}
                          style={{ left: `${pct}%` }}
                        >
                          {val}{q.unit ? ` ${q.unit}` : ''}
                        </span>
                        <input
                          type="range"
                          className={styles.slider}
                          min={min}
                          max={max}
                          step={q.step ?? 1}
                          value={val}
                          onChange={(e) => onChange(q.id, Number(e.target.value))}
                        />
                      </div>

                      <span className={styles.sliderEndLabel}>{q.maxLabel || q.rightLabel || 'High'}</span>
                    </div>
                  </div>
                );
              })()}

              {(q.type === 'toggle' || q.type === 'yesNo' || !q.type) && (
                <ToggleGroup
                  options={q.options}
                  value={answers[q.id]}
                  onChange={(v) => onChange(q.id, v)}
                />
              )}

              {isRedFlag && (
                <CrisisAlert>
                  This symptom requires urgent attention. Please contact your OB/GYN or go to the nearest emergency department immediately. Call 112 / 108 if you cannot travel safely.
                </CrisisAlert>
              )}
            </div>
          );
        })()}
      </CardBody>

      {/* <CardFooter
        onBack={onBack}
        onNext={onNext}
        nextLabel="Continue"
        disabled={!allAnswered}
      /> */}
      <CardFooter
  onBack={handleBack}
  onNext={handleNext}
  nextLabel={isLast ? 'Continue' : 'Next'}
  disabled={
    current?.type === 'slider' ? false :
    current?.type === 'date' ? !answers[current?.id] :
    !answers[current?.id]
  }
/>
    </>
  );
}
function ReviewScreen({ answers, sections, onEdit, onNext, onBack }) {
  return (
    <div  style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }} >
      {/* <CardBody> */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 24px" }} >
      <div className={styles.screenStepBadge}>
        <span className={styles.screenStepNum}>📋</span> Review
      </div>
      <h2 className={styles.screenTitle}>Review your answers</h2>
      <p className={styles.screenSubtitle}>You can edit your responses before submitting</p>
      <div className={styles.reviewList}>
        {sections.map((sec) => {
          const vals = sec.fields.map((f) => answers[f]).filter(Boolean);
          return (
            <ReviewRow
              key={sec.id}
              icon={sec.icon}
              label={sec.label}
              value={vals.length ? vals.join(' · ') : '—'}
              onEdit={() => onEdit(sec.id)}
            />
          );
        })}
      </div>
      </div>
      {/* </CardBody> */}
      <CardFooter onBack={onBack} onNext={onNext} nextLabel="Show my result" />
    </div>
  );
}

function ResultScreen({ answers, onRestart }) {
  const { pct, redFlag } = computePregScore(answers);
  const risk = getPregRisk(pct, redFlag);
  const conf = redFlag ? 98 : Math.min(72 + Math.round(pct * 0.10), 92);

  return (
    // <>
    <div className={styles.cardBodyFull}>
      <h2 className={styles.screenTitle}>Your Pregnancy Risk Result</h2>
      <p className={styles.resultDisclaimer}>This is not a medical diagnosis. Please consult your healthcare provider.</p>

      {redFlag && (
        <CrisisAlert>
          You have reported a high-risk symptom. Please contact your doctor or go to the nearest emergency department now. Call 112 / 108 if you need an ambulance.
        </CrisisAlert>
      )}

      <ResultGauge
        pct={redFlag ? 100 : pct}
        riskLevel={risk.level}
        riskColor={risk.color}
        riskBg={risk.bg}
        riskBorder={risk.border}
        riskEmoji={risk.emoji}
        riskDesc={risk.desc}
      />
      {!redFlag && <ConfidenceBar pct={conf} color={risk.color} />}

      <div className={styles.resultMeansCard}>
        <p className={styles.resultSectionTitle}>What this means</p>
        <p className={styles.resultMeansText}>{risk.means}</p>
      </div>

      <p className={styles.resultSectionTitle}>Recommended for you</p>
      <NextActionList actions={risk.actions} />

      <button className={styles.resultPrimaryBtn} onClick={onRestart}>
        Start a new screening <ChevronRight />
      </button>
      <button className={styles.resultSecondaryBtn}>Back to dashboard</button>

    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// ORCHESTRATOR  —  identical structure to MentalWellnessScreening
// ══════════════════════════════════════════════════════════════════════════════
const QUESTION_SCREEN_IDS = PREG_QUESTION_SCREENS.map((s) => s.id);

export default function PregnancyRiskScreening2() {
  const [flowIndex, setFlowIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [concerns, setConcerns] = useState([]);
const qIdxMapRef = useRef({});
  const currentId = PREG_FLOW[flowIndex];
  // const goNext = () => setFlowIndex((i) => Math.min(i + 1, PREG_FLOW.length - 1));
    const goNext = () => {
    setFlowIndex((i) => {
      const nextIndex = Math.min(i + 1, PREG_FLOW.length - 1);
      const nextId = PREG_FLOW[nextIndex];
      // Reset the next screen's question index so it always starts from Q1
      if (qIdxMapRef.current[nextId] !== undefined) {
        qIdxMapRef.current[nextId] = 0;
      }
      return nextIndex;
    });
  };
  const goBack = () => setFlowIndex((i) => Math.max(i - 1, 0));

  const handleAnswer = (id, val) => setAnswers((prev) => ({ ...prev, [id]: val }));
  const handleRestart = () => { setFlowIndex(0); setAnswers({}); setConcerns([]); };
  // const handleEdit = (sectionId) => {
  //   const idx = PREG_FLOW.indexOf(sectionId);
  //   if (idx !== -1) setFlowIndex(idx);
  // };
  const handleEdit = (sectionId) => {
  const idx = PREG_FLOW.indexOf(sectionId);
  if (idx !== -1) {
    qIdxMapRef.current[sectionId] = 0; // ← reset to first question
    setFlowIndex(idx);
  }
};

  const sidebarIndex = PREG_STEPS.findIndex((s) => s.id === currentId);
  const qScreenIdx = QUESTION_SCREEN_IDS.indexOf(currentId);
  const isQuestion = qScreenIdx !== -1;
  const showSidebar = !['intro', 'consent', 'result'].includes(currentId);
  const content = {
    "title": "Risk Screening Personalization",
    "description": "Use my answers to personalize my health risk screening.",
    "pageSubtitle": "Let's find or create your profile so we can personalize your risk screening.",
  }

  return (
    <AppShell accent="accentPink">
      <Card>
        {currentId !== 'intro'
          ? <CardHeader
            stepLabel={currentId !== 'result' ? PREG_STEPS.find((s) => s.id === currentId)?.label : null}
            onExit={currentId !== 'result' ? handleRestart : undefined}
          />
          : <div style={{ padding: '18px 24px 0' }}><Logo /></div>
        }

        {/* {currentId === 'intro'    && <IntroScreen    config={PREG_INTRO}    onStart={goNext} />} */}
        {currentId === 'intro' && <PregnancyWelcomeWeb
          onNext={goNext}
          eyebrow="Pregnancy Health"
          headlineAccent="pregnancy health"
          bodyText="This screening helps us understand possible health risks during pregnancy and ensure you receive the right care at the right time."

        />}
     
        {currentId === 'consent' &&
          <div className={styles.consentLayout}>

            <ConsentScreen
            
              config={RISK_SCREENING_CONSENT}
              onNext={goNext}
              // styles={cardstyles}
              onBack={goBack}
              // content={content}
            />
          </div>

        }
        {currentId === 'concerns' && (
          <div style={{ maxWidth: "560px" }}>
            <ConcernsScreen
              config={PREG_CONCERNS}
              selected={concerns}
              onChange={setConcerns}
              onNext={goNext}
              onBack={goBack}
            />
          </div>
        )}
        {isQuestion && (
          <div className={styles.cardSep}>
            <div className={styles.cardSepLeft}>
              <QuestionScreen
                key={currentId}
                screenConfig={PREG_QUESTION_SCREENS[qScreenIdx]}
                answers={answers}
                onChange={handleAnswer}
                onNext={goNext}
                onBack={goBack}
                questionIndex={qScreenIdx}
                qIdxMap={qIdxMapRef}
                totalQuestions={QUESTION_SCREEN_IDS.length}
              />
            </div>
            {/* <Sidebar steps={PREG_STEPS} currentStep={sidebarIndex} /> */}
            {showSidebar && <Sidebar steps={PREG_STEPS} currentStep={sidebarIndex} />}
          </div>
        )}
        {currentId === 'review' && (
          <ReviewScreen
            answers={answers}
            sections={PREG_REVIEW_SECTIONS}
            onEdit={handleEdit}
            onNext={goNext}
            onBack={goBack}
          />

        )}
        {currentId === 'result' && (
          <ResultScreen answers={answers} onRestart={handleRestart} />
        )}
      </Card>

      {/* {showSidebar && <Sidebar steps={PREG_STEPS} currentStep={sidebarIndex} />} */}
    </AppShell>
  );
}